<template>
  <section id="request-selection-boxes-wrapper" class="request-selection-boxes">
    <TestDriveFromHomeInformation
      class="request-selection-boxes__tdfh_container"
      isInfo
    />

    <wb-notification-host
      position="top-center"
      v-if="showTimeChangeNotification"
    >
      <wb-notification
        icon="bds/exclamation-mark-circle-outline/24"
        mode="highlight"
        @wbdismiss="toggleChangeTimeNotification(false)"
        @wbexpire="() => {}"
      >
        <!-- eslint-disable vue/no-v-text-v-html-on-component -->
        <wb-notification-content
          v-html="$t('schedule.request.notification.timeChange')"
        >
        </wb-notification-content>
        <!-- eslint-enable vue/no-v-text-v-html-on-component-->
      </wb-notification>
    </wb-notification-host>

    <h3 class="request-selection-boxes__title wb-type-heading-xs">
      {{ $t('schedule.request.selectionBoxes.title') }}
    </h3>

    <wb-accordion class="request-selection-boxes__accordion">
      <div
        class="request-selection-boxes__progress-line"
        :style="{ height: lineProgress }"
      ></div>
      <RetailerSelectionBox
        id="request-selection-boxes-retailer-ref"
        v-if="showRetailerSelectionBox"
        :currentStep="currentStep"
        :status="stateOfTheBoxes.retailer"
        :isMobile="isMobile"
        :step="steps.retailer"
        :accordionClosingHandler="accordionClosingHandler"
        @changeStatus="changeStatus(RequestSelectionBoxesStep.RETAILER)"
        @closeAccordion="closeAccordion"
        @open="setCurrentStep"
        @onBoxOpen="trackCurrentStepEvent"
        @onToggle="handleOnToggle"
      />
      <SlotSelectionBox
        id="request-selection-boxes-slot-ref"
        :currentStep="currentStep"
        :status="stateOfTheBoxes.slot"
        :isMobile="isSmallScreen"
        :step="steps.slot"
        :accordionClosingHandler="accordionClosingHandler"
        @changeStatus="changeStatus(RequestSelectionBoxesStep.SLOT)"
        @closeAccordion="closeAccordion"
        @open="setCurrentStep"
        @onBoxOpen="trackCurrentStepEvent"
        @onToggle="handleOnToggle"
      />
      <PersonalDataSelectionBox
        id="request-selection-boxes-data-ref"
        :currentStep="currentStep"
        :status="stateOfTheBoxes.personalData"
        :isMobile="isMobile"
        :step="steps.personalData"
        :accordionClosingHandler="accordionClosingHandler"
        @changeStatus="changeStatus(RequestSelectionBoxesStep.PERSONAL_DATA)"
        @closeAccordion="closeAccordion"
        @open="setCurrentStep"
        @onBoxOpen="trackCurrentStepEvent"
        @onToggle="handleOnToggle"
      />
    </wb-accordion>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, onMounted } from 'vue';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { useStore } from '../../composables/store.composable';

import { EventTrackingInfo } from '@dh-io-otb/shared/src/models/entities/tracking.model';
import TrackingService from '@dh-io-otb/shared/src/services/tracking.service';
import {
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_NAME,
} from '@dh-io-otb/shared/src/models/constants';

import {
  RequestSelectionBoxesStatus,
  RequestSelectionBoxesStep,
  RequestSelectionBoxesStepNumbers,
} from '../../models/entities/request-selection-boxes.model';

import UtilsService from '../../services/utils.service';
import DateService from '../../services/date.service';

import {
  PersonalDataSelectionBox,
  RetailerSelectionBox,
  SlotSelectionBox,
} from './index';

import { TestDriveFromHomeInformation } from '../test-drive-from-home-information/';

export default defineComponent({
  name: 'otb-request-selection-boxes',
  components: {
    PersonalDataSelectionBox,
    RetailerSelectionBox,
    SlotSelectionBox,
    TestDriveFromHomeInformation,
  },
  props: {
    goToSummaryPage: {
      type: Function as any,
    },
  },
  setup(props, { emit }) {
    const { isMobile, isSmallScreen } = useMediaQuery();
    const { commit, state, dispatch } = useStore();

    const isRetailerAccordionOpen = ref(false);
    const isSlotAccordionOpen = ref(false);
    const isPersonalDataAccordionOpen = ref(false);

    const getLastDoneStep = () => {
      const boxesState = state.otbcFlow.requestSelectionBoxesStatus;
      let lastDoneStep = RequestSelectionBoxesStep.NONE;

      const doneSteps = Object.keys(boxesState).filter(
        (key) => boxesState[key] === RequestSelectionBoxesStatus.DONE
      );
      if (doneSteps.length > 0)
        lastDoneStep = doneSteps[
          doneSteps.length - 1
        ] as RequestSelectionBoxesStep;

      return lastDoneStep;
    };

    // lifecycle
    onMounted((): void => {
      if (!showRetailerSelectionBox.value) {
        dispatch('getTestDriveRequestsCalendar');
        changeStatus(RequestSelectionBoxesStep.RETAILER);
      } else changeStatus(getLastDoneStep());
    });

    const currentStep = ref(RequestSelectionBoxesStep.NONE);
    const navigationHistory = ref([] as string[]);
    const showTimeChangeNotification = ref(false);

    const selectorRetailerChosen = ref(false);
    const selectorSlotChosen = ref(false);
    const selectorPersonalDataChosen = ref(false);

    const showRetailerSelectionBox = computed(() => {
      return !state.otbcFlow.fixedDealer;
    });

    const steps = computed(() => {
      return {
        retailer: RequestSelectionBoxesStepNumbers.FIRST,
        slot: showRetailerSelectionBox.value
          ? RequestSelectionBoxesStepNumbers.SECOND
          : RequestSelectionBoxesStepNumbers.FIRST,
        personalData: showRetailerSelectionBox.value
          ? RequestSelectionBoxesStepNumbers.THIRD
          : RequestSelectionBoxesStepNumbers.SECOND,
      };
    });

    const canSubmit = computed(() => {
      return (
        stateOfTheBoxes.value.personalData === RequestSelectionBoxesStatus.DONE
      );
    });

    const stateOfTheBoxes = computed(
      () => state.otbcFlow.requestSelectionBoxesStatus
    );

    const stateOfSelectedDealerID = computed(
      () => state.otbcFlow.selectedDealer?.id
    );

    const toggleChangeTimeNotification = (status: boolean) => {
      showTimeChangeNotification.value =
        status ?? !showTimeChangeNotification.value;
    };

    const handleOnToggle = () => emit('onAccordionToggle');

    const changeStatus = (box: string) => {
      const newBoxesState = { ...stateOfTheBoxes.value };

      const isSlotDoneInFixedDealer =
        steps.value.slot === RequestSelectionBoxesStepNumbers.FIRST &&
        newBoxesState.slot === RequestSelectionBoxesStatus.DONE;

      const isPersonalDataDoneInFixedDealer =
        steps.value.personalData === RequestSelectionBoxesStepNumbers.SECOND &&
        newBoxesState.personalData === RequestSelectionBoxesStatus.DONE;

      switch (box) {
        case RequestSelectionBoxesStep.NONE:
          if (newBoxesState.retailer !== RequestSelectionBoxesStatus.DONE) {
            newBoxesState.retailer = RequestSelectionBoxesStatus.AVAILABLE;
          }
          if (showRetailerSelectionBox.value) {
            setCurrentStep(RequestSelectionBoxesStep.RETAILER);
          }
          break;
        case RequestSelectionBoxesStep.RETAILER:
          if (
            isSmallScreen.value &&
            newBoxesState.retailer == RequestSelectionBoxesStatus.DONE
          ) {
            setCurrentStep(RequestSelectionBoxesStep.NONE);
            break;
          }

          navigationHistory.value.push(RequestSelectionBoxesStep.RETAILER);
          newBoxesState.retailer = RequestSelectionBoxesStatus.DONE;

          if (newBoxesState.slot !== RequestSelectionBoxesStatus.DONE)
            newBoxesState.slot = RequestSelectionBoxesStatus.AVAILABLE;

          if (isSmallScreen.value && isSlotDoneInFixedDealer) {
            setCurrentStep(RequestSelectionBoxesStep.NONE);
            break;
          }

          if (isPersonalDataDoneInFixedDealer) {
            setCurrentStep(RequestSelectionBoxesStep.NONE);
            break;
          } else if (isSlotDoneInFixedDealer) {
            setCurrentStep(RequestSelectionBoxesStep.PERSONAL_DATA);
            break;
          }

          setCurrentStep(RequestSelectionBoxesStep.SLOT);
          break;
        case RequestSelectionBoxesStep.SLOT:
          if (
            isSmallScreen.value &&
            newBoxesState.slot == RequestSelectionBoxesStatus.DONE
          ) {
            setCurrentStep(RequestSelectionBoxesStep.NONE);
            break;
          }

          navigationHistory.value.push(RequestSelectionBoxesStep.SLOT);
          newBoxesState.slot = RequestSelectionBoxesStatus.DONE;

          if (newBoxesState.personalData !== RequestSelectionBoxesStatus.DONE)
            newBoxesState.personalData = RequestSelectionBoxesStatus.AVAILABLE;

          setCurrentStep(RequestSelectionBoxesStep.PERSONAL_DATA);
          break;
        case RequestSelectionBoxesStep.PERSONAL_DATA:
          if (
            isSmallScreen.value &&
            newBoxesState.personalData == RequestSelectionBoxesStatus.DONE
          ) {
            setCurrentStep(RequestSelectionBoxesStep.NONE);
            break;
          }

          navigationHistory.value.push(RequestSelectionBoxesStep.PERSONAL_DATA);
          newBoxesState.personalData = RequestSelectionBoxesStatus.DONE;

          UtilsService.closeAllAccordions();
          break;
      }
      commit('requestSelectionBoxesStatus', newBoxesState);
    };

    const lineProgress = computed(() => {
      let progress = 0;

      // For retailer
      if (
        stateOfTheBoxes.value.retailer === RequestSelectionBoxesStatus.AVAILABLE
      ) {
        progress += 16;
      } else if (
        stateOfTheBoxes.value.retailer === RequestSelectionBoxesStatus.DONE
      ) {
        progress += 33;
      }

      // For slot
      if (
        stateOfTheBoxes.value.slot === RequestSelectionBoxesStatus.AVAILABLE
      ) {
        progress += 16;
      } else if (
        stateOfTheBoxes.value.slot === RequestSelectionBoxesStatus.DONE
      ) {
        progress += 33;
      }

      // For personalData
      if (
        stateOfTheBoxes.value.personalData ===
        RequestSelectionBoxesStatus.AVAILABLE
      ) {
        progress += 16;
      } else if (
        stateOfTheBoxes.value.personalData === RequestSelectionBoxesStatus.DONE
      ) {
        progress += 34; // to reach 100% if all are done
      }

      return `${progress}%`;
    });

    const updateAccordionStates = (step: RequestSelectionBoxesStep) => {
      const allClosed =
        step === RequestSelectionBoxesStep.NONE ||
        (state.otbcFlow.fixedDealer &&
          step === RequestSelectionBoxesStep.RETAILER);
      const states = {
        retailer:
          step === RequestSelectionBoxesStep.RETAILER &&
          !state.otbcFlow.fixedDealer,
        slot: step === RequestSelectionBoxesStep.SLOT,
        personalData: step === RequestSelectionBoxesStep.PERSONAL_DATA,
      };

      // Update accordion states
      isRetailerAccordionOpen.value = states.retailer && !allClosed;
      isSlotAccordionOpen.value = states.slot && !allClosed;
      isPersonalDataAccordionOpen.value = states.personalData && !allClosed;
    };

    const areAllAccordionsClosed = computed(() => {
      return (
        !isRetailerAccordionOpen.value &&
        !isSlotAccordionOpen.value &&
        !isPersonalDataAccordionOpen.value
      );
    });

    const setCurrentStep = (step: RequestSelectionBoxesStep) => {
      // Accordion has a conflicting async collapsing transition
      // which breaks if currentStep value is changed, and there is
      // no function to observe, as it is part of workbench's default
      // behaviour.
      setTimeout(() => {
        updateAccordionStates(step);
        currentStep.value = step;
        commit(
          'requestSelectionBoxesStep',
          areAllAccordionsClosed.value ? RequestSelectionBoxesStep.NONE : step
        );
      }, 100);
    };

    const closeAccordion = () => {
      const historyLength = navigationHistory.value.length;
      let previousStep;
      if (historyLength > 0) {
        previousStep = navigationHistory.value.pop();
      } else {
        previousStep = RequestSelectionBoxesStep.NONE;
      }

      setCurrentStep(previousStep);
    };

    const getActionLabelStepEvent = (step: RequestSelectionBoxesStep) => {
      const action = TRACKING_EVENT_ACTION.REQUEST_FLOW;
      let label;
      switch (step) {
        case RequestSelectionBoxesStep.RETAILER:
          label = TRACKING_EVENT_LABEL.SELECT_DEALER;
          break;
        case RequestSelectionBoxesStep.SLOT:
          label = TRACKING_EVENT_LABEL.CALENDAR;
          break;
        case RequestSelectionBoxesStep.PERSONAL_DATA:
          label = TRACKING_EVENT_LABEL.CONTACT_FORM;
          break;
      }

      return { action, label };
    };

    // the current step is always one step behind the actual step
    // because the current step is set after the accordion is opened
    // and since event is tracked before the accordion opening we use event handlers
    const trackCurrentStepEvent = (step: RequestSelectionBoxesStep) => {
      // will not track for selectors that require dealer id in event
      if (
        step !== RequestSelectionBoxesStep.RETAILER &&
        !stateOfSelectedDealerID.value
      )
        return;

      const { action, label } = getActionLabelStepEvent(step);
      if (!label || TrackingService.hasEventBeenCalled(action, label)) return;

      // since wb-accordion-item component is opening two times in OW
      // this workaround is used to make sure only one of the events is tracked
      switch (step) {
        case RequestSelectionBoxesStep.RETAILER:
          selectorRetailerChosen.value = true;
          break;
        case RequestSelectionBoxesStep.SLOT:
          selectorSlotChosen.value = true;
          break;
        case RequestSelectionBoxesStep.PERSONAL_DATA:
          selectorPersonalDataChosen.value = true;
          break;
      }
    };

    const emitStepEvent = (
      action: TRACKING_EVENT_ACTION,
      label: TRACKING_EVENT_LABEL
    ) => {
      const event: EventTrackingInfo = {
        name: TRACKING_EVENT_NAME.FEATURE,
        category: TRACKING_EVENT_CATEGORY.FEATURE,
        action,
        label,
      };

      TrackingService.trackEvent(event);
    };

    watch(
      () => state.otbcFlow.dealerCalendar,
      (newCalendar) => {
        let selectedDayIsAvailable = true;
        const shortSelectedDay = DateService.formatDateForVBS(
          state.otbcFlow.selectedDay
        );
        for (const day of newCalendar) {
          if (selectedDayIsAvailable) {
            if (day.date === shortSelectedDay) {
              if (day.available) {
                break;
              } else {
                selectedDayIsAvailable = false;
              }
            }
          } else if (day.available) {
            commit('selectedDay', new Date(day.date));
            toggleChangeTimeNotification(true);
            break;
          }
        }
      }
    );

    // watching for the slection of the dealer id to retrigger the event pushing logic
    // as some events require the dealer id to be present
    watch(stateOfSelectedDealerID, () =>
      trackCurrentStepEvent(currentStep.value)
    );

    // watchers to trigger the event pushing logic
    // that will trigger only once per renderization of this component
    // event if the value is assigned multiple times
    watch(selectorRetailerChosen, () => {
      if (!selectorRetailerChosen.value) return;
      const { action, label } = getActionLabelStepEvent(
        RequestSelectionBoxesStep.RETAILER
      );
      emitStepEvent(action, label);
    });

    watch(selectorSlotChosen, () => {
      if (!selectorSlotChosen.value) return;
      const { action, label } = getActionLabelStepEvent(
        RequestSelectionBoxesStep.SLOT
      );
      emitStepEvent(action, label);
    });

    watch(selectorPersonalDataChosen, () => {
      if (!selectorPersonalDataChosen.value) return;
      const { action, label } = getActionLabelStepEvent(
        RequestSelectionBoxesStep.PERSONAL_DATA
      );
      emitStepEvent(action, label);
    });

    watch(currentStep, () => {
      handleOnToggle();
    });

    const accordionClosingHandler = () => {
      const element = document
        ?.querySelector('otb-otbc')
        ?.shadowRoot?.querySelector(`#request-selection-boxes-wrapper`);
      if (!element) return;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    };

    return {
      canSubmit,
      currentStep,
      showTimeChangeNotification,
      stateOfTheBoxes,
      changeStatus,
      closeAccordion,
      setCurrentStep,
      toggleChangeTimeNotification,
      RequestSelectionBoxesStep,
      isMobile,
      isSmallScreen,
      lineProgress,
      showRetailerSelectionBox,
      steps,
      trackCurrentStepEvent,
      handleOnToggle,
      accordionClosingHandler,
      isRetailerAccordionOpen,
      isSlotAccordionOpen,
      isPersonalDataAccordionOpen,
      areAllAccordionsClosed,
    };
  },
});
</script>

<style scoped lang="scss" src="./request-selection-boxes.scss"></style>
